/**
 * Sound effects (asked for on 2026-09-27: "add sound effects. in particular
 * a wilhelm scream when a player dies").
 *
 * Every sound but one is made on the spot with Web Audio rather than played
 * from a file: there is nothing to download, nothing to license, and a
 * blow, a clink or a scream is a few oscillators and some shaped noise.
 *
 * Two come from files. The horn of the Challenge spell is since 2026-10-22
 * ("find a good sound for the horn of boromir, to be used as the sound for
 * challenge") a real ox horn: static/horn.mp3, 17 KB, three blasts (two
 * short, one long, the shape the built horn below has) cut with ffmpeg
 * from "R12-46-Blowing Note on Cow Horn" in the Internet Archive's "Red
 * Library: Bells, Horns, Whistles"
 * (https://archive.org/details/Red_Library_Bells_Horns_Whistles), the USC
 * optical sound effects library digitised by Craig Smith and released
 * there under CC0 1.0, taken down an octave for a great horn's depth and
 * given a little echo. Boromir's was "a great horn of the wild ox of the
 * East", so an ox horn it is.
 *
 * The other is the scream itself. Since 2026-10-22 ("change the player death
 * sound to the wilhelm scream") a player's death plays the real Wilhelm
 * scream, the 1951 Warner Bros. stock cry (most likely Sheb Wooley's) that
 * has been in every other film since: static/scream.mp3, 10 KB, a mono
 * 48 kbit/s copy of Wikimedia Commons' "Wilhelm Scream.ogg"
 * (https://commons.wikimedia.org/wiki/File:Wilhelm_Scream.ogg), which is
 * released there under the CC0 1.0 public domain dedication, from the
 * Internet Archive's SSE_Library_VOICES. It is fetched once the page has
 * its audio context and decoded into a buffer; until it arrives, or if it
 * never does, the imitation below screams in its place: a buzz with a
 * falling pitch and a shiver in it, pushed through the three resonances of
 * an open "aah", in the famous three parts, a yelp, a catch, and the long
 * fall.
 *
 * The sounds hang off the effects the server already sends, so a sound is
 * heard exactly where something is seen. Each is quieter the further off
 * it is and leans left or right with where it is on the screen, so a fight
 * behind a wall to the east is heard to the east.
 */

import type { Effect } from "./types";
import { Music, trackFor } from "./music";

/** The name of every sound there is, and what each is for. */
export type SoundName =
  | "hit" | "clink" | "spark" | "whoosh" | "twang" | "fall" | "scream"
  | "levelup" | "fire" | "zap" | "shimmer" | "splash" | "rumble" | "crack" | "chime" | "shriek"
  | "horn";

/**
 * The sound an effect makes, or null for one that is silent. Heals, xp and
 * damage numbers are left quiet on purpose: a potion pays out ten times a
 * second, and every blow that lands already makes a noise as the blood.
 */
export function soundFor(e: Pick<Effect, "kind" | "value">): SoundName | null {
  switch (e.kind) {
    case "blood":
      return "hit";
    case "block":
      return "clink";
    case "spark":
    case "immune":
      return "spark";
    case "miss":
    case "wide":
    case "web":
    case "vanish":
      return "whoosh";
    case "shriek":
      return "shriek";
    case "shot":
      return "twang";
    case "lava":
    case "fire":
    case "nova":
      return "fire";
    case "death":
      // A player's death is marked with a 1 by the server.
      return e.value === 1 ? "scream" : "fall";
    case "levelup":
      return "levelup";
    case "arc":
      return "zap";
    case "beam":
    case "shaft":
      return "shimmer";
    case "splash":
      return "splash";
    case "rift":
    case "sweep":
      return "rumble";
    case "break":
    case "crit":
    case "whip":
      return "crack";
    case "lure":
    case "summon":
      return "shimmer";
    case "challenge":
      return "horn";
    case "find":
    case "provoke":
    case "elbereth":
    case "convince":
    case "haste":
    case "light":
    case "bond":
    case "unbond":
    case "cleanse":
    case "mend":
    case "sanctify":
    case "crown":
      return "chime";
  }
  return null;
}

/**
 * How loud, and how far left or right, a sound is heard from dx, dy tiles
 * away: full at your feet, half at four tiles, and panned by how far across
 * the nine-tile half-window it is.
 */
export function placement(dx: number, dy: number): { gain: number; pan: number } {
  const d = Math.hypot(dx, dy);
  return { gain: 1 / (1 + d / 4), pan: Math.max(-1, Math.min(1, dx / 9)) };
}

/**
 * How soon the same sound may play again, in milliseconds. A dragon's wave
 * arrives a tile at a time in one instant, and forty fires at once is one
 * roar, not forty.
 */
const AGAIN_MS: Partial<Record<SoundName, number>> = {
  fire: 250,
  rumble: 400,
  zap: 120,
  chime: 150,
  // Three blasts take two seconds; two challenges at once are one horn.
  horn: 1500,
  scream: 0,
};
const DEFAULT_AGAIN_MS = 35;

type Ctx = BaseAudioContext;

function noiseBuffer(ctx: Ctx): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * 1.5);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

/** A gain that rises to peak in `a` seconds and falls away over `r`. */
function env(ctx: Ctx, out: AudioNode, t: number, peak: number, a: number, r: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + a);
  g.gain.exponentialRampToValueAtTime(0.0001, t + a + r);
  g.connect(out);
  return g;
}

function osc(ctx: Ctx, type: OscillatorType, f: number, to: AudioNode, t: number, dur: number): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(f, t);
  o.connect(to);
  o.start(t);
  o.stop(t + dur);
  return o;
}

function noise(ctx: Ctx, buf: AudioBuffer, to: AudioNode, t: number, dur: number): AudioBufferSourceNode {
  const n = ctx.createBufferSource();
  n.buffer = buf;
  n.connect(to);
  n.start(t, Math.random() * 0.5);
  n.stop(t + dur);
  return n;
}

function filter(ctx: Ctx, type: BiquadFilterType, f: number, q: number, to: AudioNode): BiquadFilterNode {
  const b = ctx.createBiquadFilter();
  b.type = type;
  b.frequency.value = f;
  b.Q.value = q;
  b.connect(to);
  return b;
}

/**
 * The scream: three cries through the formants of an open "aah". Each cry
 * is [start, length, first pitch, top pitch, last pitch, loudness].
 */
const SCREAM: [number, number, number, number, number, number][] = [
  [0.0, 0.17, 540, 830, 760, 0.8],
  [0.21, 0.2, 690, 870, 650, 0.65],
  [0.45, 0.9, 950, 1010, 420, 1.0],
];

function scream(ctx: Ctx, out: AudioNode, t: number, buf: AudioBuffer) {
  const voice = ctx.createGain();
  voice.gain.value = 0.9;
  voice.connect(out);
  // The resonances of the mouth open on "aah", and a little of the raw
  // buzz for the rasp in it.
  const mouth = ctx.createGain();
  for (const [f, q, g] of [[820, 6, 1], [1250, 7, 0.7], [2650, 9, 0.35], [3500, 10, 0.15]] as const) {
    const lvl = ctx.createGain();
    lvl.gain.value = g;
    lvl.connect(voice);
    mouth.connect(filter(ctx, "bandpass", f, q, lvl));
  }
  const rasp = ctx.createGain();
  rasp.gain.value = 0.06;
  rasp.connect(voice);
  mouth.connect(filter(ctx, "lowpass", 3200, 0.7, rasp));
  for (const [at, len, f0, top, f1, amp] of SCREAM) {
    const s = t + at;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, s);
    g.gain.exponentialRampToValueAtTime(amp, s + 0.025);
    g.gain.setValueAtTime(amp, s + len * 0.7);
    g.gain.exponentialRampToValueAtTime(0.0001, s + len);
    g.connect(mouth);
    for (const detune of [0, 7]) {
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.detune.value = detune;
      o.frequency.setValueAtTime(f0, s);
      o.frequency.linearRampToValueAtTime(top, s + len * 0.25);
      o.frequency.exponentialRampToValueAtTime(f1, s + len);
      // The shiver: a vibrato of a few in the hundred.
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 6.5;
      const depth = ctx.createGain();
      depth.gain.value = top * 0.025;
      lfo.connect(depth);
      depth.connect(o.frequency);
      o.connect(g);
      o.start(s);
      o.stop(s + len + 0.02);
      lfo.start(s);
      lfo.stop(s + len + 0.02);
    }
    // Breath under the voice.
    const breath = env(ctx, mouth, s, amp * 0.25, 0.02, len);
    noise(ctx, buf, filter(ctx, "bandpass", 1800, 1, breath), s, len + 0.02);
  }
}

/**
 * The horn: a challenge's three blasts, as Boromir blows the Horn of Gondor
 * (2026-10-21: "use 3 trumpet blasts like boromir has"). Each blast is
 * [start, length]: two short calls and a long one. A blast is a brassy
 * buzz — a sawtooth and its fifth an octave up — that the lips push up into
 * its note, held, and let fall a little as the breath gives out, through a
 * lowpass that opens as it swells, which is what makes brass bright when
 * it is blown hard.
 */
const HORN: [number, number][] = [
  [0.0, 0.32],
  [0.42, 0.32],
  [0.84, 1.0],
];
function horn(ctx: Ctx, out: AudioNode, t0: number) {
  const f = 174.6; // F3, low and wide like a great horn
  for (const [at, len] of HORN) {
    const t = t0 + at;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.32, t + 0.06);
    g.gain.setValueAtTime(0.32, t + len - 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    g.connect(out);
    const lp = filter(ctx, "lowpass", 600, 1.2, g);
    lp.frequency.setValueAtTime(600, t);
    lp.frequency.exponentialRampToValueAtTime(2200, t + 0.08);
    lp.frequency.exponentialRampToValueAtTime(1300, t + len);
    for (const [mul, type] of [[1, "sawtooth"], [3, "triangle"]] as [number, OscillatorType][]) {
      const o = osc(ctx, type, f * mul * 0.94, lp, t, len + 0.02);
      o.frequency.exponentialRampToValueAtTime(f * mul, t + 0.05);
      o.frequency.setValueAtTime(f * mul, t + len - 0.15);
      o.frequency.exponentialRampToValueAtTime(f * mul * 0.97, t + len);
    }
  }
}

/** The sounds that come from a file rather than being built, once decoded. */
export type Samples = Partial<Record<SoundName, AudioBuffer>>;

/** The file each sampled sound is in, under the page's root. */
export const SAMPLE_FILES: Partial<Record<SoundName, string>> = {
  scream: "scream.mp3",
  horn: "horn.mp3",
};

/**
 * Plays one sound into `out` at time `t`. Written against BaseAudioContext
 * so the same code renders into an OfflineAudioContext for checking. A
 * sound whose recording is in `samples` plays that; the rest are built.
 */
export function render(ctx: Ctx, out: AudioNode, name: SoundName, t: number, buf: AudioBuffer = noiseBuffer(ctx), samples: Samples = {}) {
  const sample = samples[name];
  if (sample) {
    const g = ctx.createGain();
    g.gain.value = 0.9;
    g.connect(out);
    const src = ctx.createBufferSource();
    src.buffer = sample;
    src.connect(g);
    src.start(t);
    return;
  }
  switch (name) {
    case "hit": {
      const g = env(ctx, out, t, 0.6, 0.004, 0.16);
      const o = osc(ctx, "sine", 150, g, t, 0.2);
      o.frequency.exponentialRampToValueAtTime(55, t + 0.15);
      noise(ctx, buf, filter(ctx, "lowpass", 900, 0.7, env(ctx, out, t, 0.35, 0.002, 0.06)), t, 0.08);
      break;
    }
    case "clink": {
      const g = env(ctx, out, t, 0.18, 0.002, 0.25);
      osc(ctx, "triangle", 1850, g, t, 0.3);
      osc(ctx, "triangle", 2710, g, t, 0.3);
      noise(ctx, buf, filter(ctx, "highpass", 4000, 0.7, env(ctx, out, t, 0.12, 0.001, 0.03)), t, 0.05);
      break;
    }
    case "spark": {
      noise(ctx, buf, filter(ctx, "highpass", 3000, 0.7, env(ctx, out, t, 0.14, 0.001, 0.05)), t, 0.07);
      osc(ctx, "triangle", 3300, env(ctx, out, t, 0.08, 0.001, 0.07), t, 0.1);
      break;
    }
    case "whoosh": {
      const bp = filter(ctx, "bandpass", 400, 1.5, env(ctx, out, t, 0.3, 0.05, 0.2));
      bp.frequency.setValueAtTime(400, t);
      bp.frequency.exponentialRampToValueAtTime(1800, t + 0.22);
      noise(ctx, buf, bp, t, 0.3);
      break;
    }
    case "twang": {
      const o = osc(ctx, "triangle", 230, env(ctx, out, t, 0.28, 0.003, 0.28), t, 0.32);
      o.frequency.exponentialRampToValueAtTime(190, t + 0.28);
      noise(ctx, buf, filter(ctx, "bandpass", 2500, 2, env(ctx, out, t, 0.1, 0.001, 0.04)), t, 0.05);
      break;
    }
    case "fall": {
      const lp = filter(ctx, "lowpass", 700, 1, env(ctx, out, t, 0.35, 0.02, 0.5));
      const o = osc(ctx, "sawtooth", 170, lp, t, 0.55);
      o.frequency.exponentialRampToValueAtTime(55, t + 0.5);
      noise(ctx, buf, filter(ctx, "lowpass", 300, 0.7, env(ctx, out, t + 0.35, 0.5, 0.005, 0.2)), t + 0.35, 0.25);
      break;
    }
    case "scream":
      // The imitation, for while the recording has not arrived.
      scream(ctx, out, t, buf);
      break;
    case "shriek": {
      // The Witch-king's cry (2026-10-12): high, thin and cold, climbing
      // and then sliding away, with a shiver in it and breath round it.
      // Thinner than a player's scream, which goes through an open mouth;
      // this goes through nothing at all.
      const len = 1.3;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.32, t + 0.08);
      g.gain.setValueAtTime(0.32, t + len * 0.6);
      g.gain.exponentialRampToValueAtTime(0.0001, t + len);
      g.connect(out);
      const bp = filter(ctx, "bandpass", 1800, 2, g);
      for (const [type, detune] of [["sawtooth", 0], ["sine", 11], ["sine", -13]] as const) {
        const o = ctx.createOscillator();
        o.type = type;
        o.detune.value = detune;
        o.frequency.setValueAtTime(900, t);
        o.frequency.exponentialRampToValueAtTime(1500, t + len * 0.3);
        o.frequency.exponentialRampToValueAtTime(700, t + len);
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 9;
        const depth = ctx.createGain();
        depth.gain.value = 40;
        lfo.connect(depth);
        depth.connect(o.frequency);
        o.connect(bp);
        o.start(t);
        o.stop(t + len + 0.02);
        lfo.start(t);
        lfo.stop(t + len + 0.02);
      }
      noise(ctx, buf, filter(ctx, "highpass", 3500, 0.7, env(ctx, out, t, 0.08, 0.1, len)), t, len + 0.1);
      break;
    }
    case "levelup": {
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
        const s = t + i * 0.09;
        osc(ctx, "triangle", f, env(ctx, out, s, 0.22, 0.01, 0.35), s, 0.4);
      });
      break;
    }
    case "fire": {
      const lp = filter(ctx, "lowpass", 500, 0.8, env(ctx, out, t, 0.4, 0.12, 0.5));
      lp.frequency.setValueAtTime(500, t);
      lp.frequency.exponentialRampToValueAtTime(1400, t + 0.2);
      lp.frequency.exponentialRampToValueAtTime(400, t + 0.6);
      noise(ctx, buf, lp, t, 0.65);
      break;
    }
    case "zap": {
      const g = env(ctx, out, t, 0.25, 0.002, 0.25);
      const o = osc(ctx, "square", 90, filter(ctx, "highpass", 600, 0.7, g), t, 0.28);
      o.frequency.setValueAtTime(90, t);
      o.frequency.linearRampToValueAtTime(60, t + 0.25);
      noise(ctx, buf, filter(ctx, "highpass", 2500, 0.7, g), t, 0.28);
      break;
    }
    case "shimmer": {
      for (const [f, to] of [[400, 1600], [800, 3200]] as const) {
        const o = osc(ctx, "sine", f, env(ctx, out, t, 0.12, 0.1, 0.55), t, 0.7);
        o.frequency.exponentialRampToValueAtTime(to, t + 0.6);
      }
      break;
    }
    case "splash": {
      noise(ctx, buf, filter(ctx, "bandpass", 1200, 0.8, env(ctx, out, t, 0.35, 0.01, 0.35)), t, 0.4);
      break;
    }
    case "rumble": {
      noise(ctx, buf, filter(ctx, "lowpass", 180, 1, env(ctx, out, t, 0.7, 0.08, 0.8)), t, 0.9);
      break;
    }
    case "crack": {
      noise(ctx, buf, filter(ctx, "bandpass", 1800, 3, env(ctx, out, t, 0.3, 0.001, 0.08)), t, 0.1);
      break;
    }
    case "chime": {
      const g = env(ctx, out, t, 0.12, 0.01, 0.45);
      osc(ctx, "sine", 880, g, t, 0.5);
      osc(ctx, "sine", 1320, g, t, 0.5);
      break;
    }
    case "horn":
      // The imitation, for while the recording has not arrived.
      horn(ctx, out, t);
      break;
  }
}

const OFF_KEY = "tinytibia.soundOff";
const MUSIC_OFF_KEY = "tinytibia.musicOff";

/**
 * The page's sounds: an audio context made on the first key or click (a
 * browser plays nothing before one), two switches that are remembered —
 * the noises and the music — and the one entry point the effect stream
 * calls.
 */
export class Sounds {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buf: AudioBuffer | null = null;
  private samples: Samples = {};
  private last = new Map<SoundName, number>();
  // The music for the level you stand on (asked for on 2026-09-28: "make a
  // general ground level, above ground level, and cave music"), behind a
  // switch of its own since 2026-10-10 ("add a separate music on/off in the
  // menu"). See music.ts. The level is kept from before there is any
  // audio, so the first click starts the right piece, and so is whose land
  // you are on, since the ground east of the Misty Mountains and Mordor
  // have pieces of their own (2026-10-12: "Add new overland music when east
  // of the misty mountains", "Create a music for Mordor").
  private music: Music | null = null;
  private z: number | undefined;
  private land = "";
  /** Whether the world's noises are heard. */
  on: boolean;
  /** Whether the music plays. */
  musicOn: boolean;

  constructor() {
    let off = false;
    let musicOff = false;
    try {
      off = localStorage.getItem(OFF_KEY) === "1";
      // Before music had its own switch, Sound off silenced it too, so
      // somebody who turned that off keeps the quiet they chose.
      const m = localStorage.getItem(MUSIC_OFF_KEY);
      musicOff = m === null ? off : m === "1";
    } catch {
      // No storage: both are on, and the switches last until the tab closes.
    }
    this.on = !off;
    this.musicOn = !musicOff;
    const wake = () => this.wake();
    window.addEventListener("pointerdown", wake, true);
    window.addEventListener("keydown", wake, true);
  }

  /** Turns the noises on or off, and remembers which. */
  set(on: boolean) {
    this.on = on;
    remember(OFF_KEY, on);
    if (on) this.wake();
  }

  /** Turns the music on or off, and remembers which. */
  setMusic(on: boolean) {
    this.musicOn = on;
    remember(MUSIC_OFF_KEY, on);
    if (on) this.wake();
    else this.music?.play(null);
  }

  /** The music for standing on level `z` in `land` (the server's
   *  You.music), faded across when it changes. */
  ambient(z: number | undefined, land = "") {
    this.z = z;
    this.land = land;
    if (this.musicOn) this.music?.play(trackFor(z, land));
  }

  private wake() {
    if (!this.on && !this.musicOn) return;
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    if (!this.ctx) {
      this.ctx = new AC();
      const comp = this.ctx.createDynamicsCompressor();
      comp.connect(this.ctx.destination);
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.6;
      this.master.connect(comp);
      this.buf = noiseBuffer(this.ctx);
      this.music = new Music(this.ctx, this.master, this.buf);
      for (const [name, file] of Object.entries(SAMPLE_FILES) as [SoundName, string][]) void this.load(name, file);
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    if (this.musicOn) this.music?.play(trackFor(this.z, this.land));
  }

  /** Fetches and decodes a recorded sound; a failure leaves the built one. */
  private async load(name: SoundName, file: string) {
    try {
      const res = await fetch(file);
      if (!res.ok || !this.ctx) return;
      this.samples[name] = await this.ctx.decodeAudioData(await res.arrayBuffer());
    } catch {
      // No recording: the imitation plays instead.
    }
  }

  /** The sound for an effect, heard from where `me` stands. */
  effect(e: Effect, me: { x: number; y: number; z?: number } | undefined) {
    const name = soundFor(e);
    if (name) this.play(name, me ? e.x - me.x : 0, me ? e.y - me.y : 0);
  }

  play(name: SoundName, dx = 0, dy = 0) {
    if (!this.on || !this.ctx || !this.master || !this.buf || this.ctx.state !== "running") return;
    const now = this.ctx.currentTime * 1000;
    const again = AGAIN_MS[name] ?? DEFAULT_AGAIN_MS;
    if (now - (this.last.get(name) ?? -Infinity) < again) return;
    this.last.set(name, now);
    const { gain, pan } = placement(dx, dy);
    const g = this.ctx.createGain();
    g.gain.value = gain;
    const p = this.ctx.createStereoPanner();
    p.pan.value = pan;
    g.connect(p);
    p.connect(this.master);
    render(this.ctx, g, name, this.ctx.currentTime + 0.005, this.buf, this.samples);
    // Let go of the chain once it has fallen silent: no built sound lasts
    // two seconds, and a recording says how long it is.
    const len = Math.max(2, (this.samples[name]?.duration ?? 0) + 0.1);
    setTimeout(() => {
      g.disconnect();
      p.disconnect();
    }, len * 1000);
  }
}

/** Stores a switch as the "1 means off" its key has always held. */
function remember(key: string, on: boolean) {
  try {
    localStorage.setItem(key, on ? "0" : "1");
  } catch {
    // Remembered for this tab only.
  }
}
